import {
    useCallback, useState, useEffect, useRef,
    useMemo,
} from 'react';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import { useTranslation } from 'react-i18next';
import { memberIdSelector } from 'redux-store/auth/authSelectors';
import { useSelector } from 'react-redux';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';

import { useCreateProposal } from '../CreateProposalManager';

export const useProposalAgentInformation = () => {
    const {
        is3bProductGroupSelector,
        agentInformation,
        setAgentInformationAction,
    } = useCreateProposal();

    const { t } = useTranslation();
    const memberId = useSelector(memberIdSelector);

    // --- State ---
    const [selectedGeneralAgency, setSelectedGeneralAgency] = useState<string | number | null>(null);

    const { data: generalAgencies, isLoading: isGeneralAgenciesLoading } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getGeneralAgencies',
        enabled: true,
        staleTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const initialAgent = memberId;
    const [selectedAgent, setSelectedAgent] = useState<string | number | null>(initialAgent || null);
    const [agentFilter, setAgentFilter] = useState<string>('');

    useEffect(() => {
        setSelectedGeneralAgency(agentInformation?.generalAgency?.id ?? null);
        setSelectedAgent(agentInformation?.advisor?.id ?? (initialAgent || null));
    }, [agentInformation, initialAgent]);

    const { data: membersData, isLoading: searchAgentsInProgress } = useGetCustomService({
        service: 'customMembersService',
        api: 'getMembers',
        payload: { filter: agentFilter, currentId: selectedAgent || initialAgent },
        enabled: true,
        staleTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (selectedAgent === null) {
            setSelectedAgent(initialAgent as string);
        } else {
            setAgentFilter('');
        }
    }, [initialAgent, selectedAgent]);

    // --- API Interactions ---
    const getAgentInformation = (filter: string = '') => setAgentFilter(filter);

    const hasFetchedInitial = useRef(false);

    useEffect(() => {
        if (!hasFetchedInitial.current && memberId) {
            hasFetchedInitial.current = true;
            getAgentInformation('');
        }
    }, [memberId, getAgentInformation]);

    const selectedAgencyInfo = useMemo(() => generalAgencies?.data?.find(f => f.id === parseInt(selectedGeneralAgency as string, 10)), [selectedGeneralAgency, generalAgencies?.data]);
    const selectedAgentInfo = useMemo(() => membersData?.data?.find(f => f.id === parseInt(selectedAgent as string, 10)), [selectedAgent, membersData?.data]);

    const onContinue = useCallback(async () => {
        // Logic on continue
        setAgentInformationAction({
            generalAgency: selectedAgencyInfo,
            advisor: selectedAgentInfo,
        });
        setStepComplete(true);
    }, [selectedAgencyInfo, selectedAgentInfo, setAgentInformationAction]);

    const { setStepComplete } = useLayoutController({
        callbackOnContinue: onContinue,
        isContinueDisabled: false,
        isBackDisabled: false,
        reSubscriptionStates: [is3bProductGroupSelector, selectedAgencyInfo, selectedAgentInfo],
    });

    return {
        t,
        validation: {},
        generalAgencies: generalAgencies?.data || [],
        selectedGeneralAgency,
        isLoading: isGeneralAgenciesLoading,
        agentsList: membersData?.data || [],
        selectedAgent,
        searchAgentsInProgress,
        isPreloadInProgress: false,
        selectedAgencyInfo,
        selectedAgentInfo,
        setSelectedGeneralAgency,
        setSelectedAgent,
        handleSearchAgents: getAgentInformation,
    };
};
